/**
 * Elevation Item Service
 *
 * CRUD operations for elevation_items table.
 * Handles product items, generic items, and annotations in elevation views.
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type { Database } from '@/types/database.types';
import { logger } from '@/lib/logger';
import {
  type ElevationDimensions,
} from '@/lib/elevationUnits';

type ElevationItem = Database['public']['Tables']['elevation_items']['Row'];
type ElevationItemInsert = Database['public']['Tables']['elevation_items']['Insert'];
type ElevationItemUpdate = Database['public']['Tables']['elevation_items']['Update'];

// Item types
export type ElevationItemType = 'fixture' | 'control' | 'product' | 'annotation' | 'dimension';

// Position in real-world units (feet or meters)
export interface Position {
  x: number;
  y: number;
}

// Generic dimensions stored in JSON
export interface GenericDimensionsData {
  width: number;
  height: number;
  depth?: number;
  unit: string;
  name?: string;
}

// Create product item input
export interface CreateProductItemInput {
  elevationId: string;
  productId: string;
  position: Position;
  dimensions: ElevationDimensions;
  customName?: string;
}

// Create generic item input
export interface CreateGenericItemInput {
  elevationId: string;
  itemType: ElevationItemType;
  position: Position;
  dimensions: ElevationDimensions;
  customName: string;
  unit: string;
}

// Create item input (general)
export interface CreateItemInput {
  elevationId: string;
  itemType: ElevationItemType;
  position: Position;
  width?: number;
  height?: number;
  productId?: string;
  isGeneric?: boolean;
  genericDimensions?: GenericDimensionsData;
  customName?: string;
  metadata?: Record<string, unknown>;
  annotationData?: Record<string, unknown>;
  annotationType?: string;
}

// Update item input
export interface UpdateItemInput {
  position?: Position;
  width?: number;
  height?: number;
  rotation?: number;
  scale?: number;
  customName?: string;
  metadata?: Record<string, unknown>;
  isLocked?: boolean;
  isVisible?: boolean;
  layerOrder?: number;
}

export const elevationItemService = {
  /**
   * Get all items for an elevation
   */
  async getItems(elevationId: string): Promise<ElevationItem[]> {
    const supabase = await getAuthenticatedSupabaseClient();

    const { data, error } = await supabase
      .from('elevation_items')
      .select('*')
      .eq('elevation_id', elevationId)
      .order('layer_order', { ascending: true });

    if (error) {
      logger.error('Error fetching elevation items:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get a single item by ID
   */
  async getItem(itemId: string): Promise<ElevationItem | null> {
    const supabase = await getAuthenticatedSupabaseClient();

    const { data, error } = await supabase
      .from('elevation_items')
      .select('*')
      .eq('id', itemId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      logger.error('Error fetching elevation item:', error);
      throw error;
    }

    return data;
  },

  /**
   * Create a new item
   */
  async createItem(input: CreateItemInput): Promise<ElevationItem> {
    const supabase = await getAuthenticatedSupabaseClient();

    // Get the next layer order
    const { data: existingItems } = await supabase
      .from('elevation_items')
      .select('layer_order')
      .eq('elevation_id', input.elevationId)
      .order('layer_order', { ascending: false })
      .limit(1);

    const nextLayerOrder = existingItems?.[0]?.layer_order
      ? (existingItems[0].layer_order + 1)
      : 1;

    const insertData: ElevationItemInsert = {
      elevation_id: input.elevationId,
      item_type: input.itemType,
      x_position: input.position.x,
      y_position: input.position.y,
      width: input.width,
      height: input.height,
      product_id: input.productId,
      is_generic: input.isGeneric ?? false,
      generic_dimensions: input.genericDimensions as unknown as Database['public']['Tables']['elevation_items']['Insert']['generic_dimensions'],
      custom_name: input.customName,
      metadata: input.metadata as unknown as Database['public']['Tables']['elevation_items']['Insert']['metadata'],
      annotation_data: input.annotationData as unknown as Database['public']['Tables']['elevation_items']['Insert']['annotation_data'],
      annotation_type: input.annotationType,
      layer_order: nextLayerOrder,
      is_visible: true,
      is_locked: false,
    };

    const { data, error } = await supabase
      .from('elevation_items')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      logger.error('Error creating elevation item:', error);
      throw error;
    }

    return data;
  },

  /**
   * Create a product item (linked to product_library)
   */
  async createProductItem(input: CreateProductItemInput): Promise<ElevationItem> {
    return this.createItem({
      elevationId: input.elevationId,
      itemType: 'product',
      position: input.position,
      width: input.dimensions.width,
      height: input.dimensions.height,
      productId: input.productId,
      isGeneric: false,
      customName: input.customName,
      metadata: {
        depth: input.dimensions.depth,
      },
    });
  },

  /**
   * Create a generic item (not linked to product_library)
   */
  async createGenericItem(input: CreateGenericItemInput): Promise<ElevationItem> {
    const genericDimensions: GenericDimensionsData = {
      width: input.dimensions.width,
      height: input.dimensions.height,
      depth: input.dimensions.depth,
      unit: input.unit,
      name: input.customName,
    };

    return this.createItem({
      elevationId: input.elevationId,
      itemType: input.itemType,
      position: input.position,
      width: input.dimensions.width,
      height: input.dimensions.height,
      isGeneric: true,
      genericDimensions,
      customName: input.customName,
    });
  },

  /**
   * Update an item
   */
  async updateItem(itemId: string, updates: UpdateItemInput): Promise<ElevationItem> {
    const supabase = await getAuthenticatedSupabaseClient();

    const updateData: ElevationItemUpdate = {};

    if (updates.position) {
      updateData.x_position = updates.position.x;
      updateData.y_position = updates.position.y;
    }
    if (updates.width !== undefined) updateData.width = updates.width;
    if (updates.height !== undefined) updateData.height = updates.height;
    if (updates.rotation !== undefined) updateData.rotation = updates.rotation;
    if (updates.scale !== undefined) updateData.scale = updates.scale;
    if (updates.customName !== undefined) updateData.custom_name = updates.customName;
    if (updates.metadata !== undefined) {
      updateData.metadata = updates.metadata as unknown as Database['public']['Tables']['elevation_items']['Update']['metadata'];
    }
    if (updates.isLocked !== undefined) updateData.is_locked = updates.isLocked;
    if (updates.isVisible !== undefined) updateData.is_visible = updates.isVisible;
    if (updates.layerOrder !== undefined) updateData.layer_order = updates.layerOrder;

    const { data, error } = await supabase
      .from('elevation_items')
      .update(updateData)
      .eq('id', itemId)
      .select()
      .single();

    if (error) {
      logger.error('Error updating elevation item:', error);
      throw error;
    }

    return data;
  },

  /**
   * Delete an item
   */
  async deleteItem(itemId: string): Promise<void> {
    const supabase = await getAuthenticatedSupabaseClient();

    const { error } = await supabase
      .from('elevation_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      logger.error('Error deleting elevation item:', error);
      throw error;
    }
  },

  /**
   * Delete multiple items
   */
  async deleteItems(itemIds: string[]): Promise<void> {
    const supabase = await getAuthenticatedSupabaseClient();

    const { error } = await supabase
      .from('elevation_items')
      .delete()
      .in('id', itemIds);

    if (error) {
      logger.error('Error deleting elevation items:', error);
      throw error;
    }
  },

  /**
   * Update position of an item
   */
  async updatePosition(itemId: string, position: Position): Promise<ElevationItem> {
    return this.updateItem(itemId, { position });
  },

  /**
   * Update dimensions of an item
   */
  async updateDimensions(
    itemId: string,
    dimensions: { width: number; height: number }
  ): Promise<ElevationItem> {
    return this.updateItem(itemId, {
      width: dimensions.width,
      height: dimensions.height,
    });
  },

  /**
   * Clone an item
   */
  async cloneItem(
    itemId: string,
    offset: Position = { x: 0.5, y: 0.5 }
  ): Promise<ElevationItem> {
    const item = await this.getItem(itemId);
    if (!item) {
      throw new Error('Item not found');
    }

    return this.createItem({
      elevationId: item.elevation_id,
      itemType: item.item_type as ElevationItemType,
      position: {
        x: item.x_position + offset.x,
        y: item.y_position + offset.y,
      },
      width: item.width ?? undefined,
      height: item.height ?? undefined,
      productId: item.product_id ?? undefined,
      isGeneric: item.is_generic ?? undefined,
      genericDimensions: item.generic_dimensions as unknown as GenericDimensionsData,
      customName: item.custom_name ?? undefined,
      metadata: item.metadata as Record<string, unknown>,
    });
  },

  /**
   * Reorder items (update layer_order)
   */
  async reorderItems(
    itemIds: string[],
    startOrder: number = 1
  ): Promise<void> {
    const supabase = await getAuthenticatedSupabaseClient();

    // Update each item's layer_order
    const updates = itemIds.map((id, index) => ({
      id,
      layer_order: startOrder + index,
    }));

    for (const update of updates) {
      const { error } = await supabase
        .from('elevation_items')
        .update({ layer_order: update.layer_order })
        .eq('id', update.id);

      if (error) {
        logger.error('Error reordering elevation item:', error);
        throw error;
      }
    }
  },

  /**
   * Bring item to front
   */
  async bringToFront(itemId: string, elevationId: string): Promise<void> {
    const supabase = await getAuthenticatedSupabaseClient();

    // Get max layer_order
    const { data: items } = await supabase
      .from('elevation_items')
      .select('layer_order')
      .eq('elevation_id', elevationId)
      .order('layer_order', { ascending: false })
      .limit(1);

    const maxOrder = items?.[0]?.layer_order ?? 0;

    await this.updateItem(itemId, { layerOrder: maxOrder + 1 });
  },

  /**
   * Send item to back
   */
  async sendToBack(itemId: string, elevationId: string): Promise<void> {
    const supabase = await getAuthenticatedSupabaseClient();

    // Get min layer_order
    const { data: items } = await supabase
      .from('elevation_items')
      .select('layer_order')
      .eq('elevation_id', elevationId)
      .order('layer_order', { ascending: true })
      .limit(1);

    const minOrder = items?.[0]?.layer_order ?? 1;

    await this.updateItem(itemId, { layerOrder: Math.max(0, minOrder - 1) });
  },
};

export default elevationItemService;
